import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { ihm_viewer__dictionary, locale } from "../../Application/i18n/i18nViewer"

const sGatewayUrl = process.env.REACT_APP_U1_GATEWAY_BASE_URL

const getAnnouncementDismissStorageKey = (in_oAnnouncement) => {
  const sUniqueId = in_oAnnouncement?.id
    || [
      in_oAnnouncement?.maintenanceStartDate || in_oAnnouncement?.startDate || '',
      in_oAnnouncement?.maintenanceTimezone || '',
      in_oAnnouncement?.title || ''
    ].join('|')

  return `orthup:announcement:dismissed:${sUniqueId}`
}

const getAnnouncementDismissExpiresAt = (in_oAnnouncement) => {
  return in_oAnnouncement?.endDate || in_oAnnouncement?.maintenanceEndDate || null
}

const normalizeDateString = (in_dateValue) => {
  if (!in_dateValue) return null

  const nTimestamp = new Date(in_dateValue).getTime()
  if (Number.isNaN(nTimestamp)) return null

  return new Date(nTimestamp).toISOString()
}

const getAnnouncementMaintenanceSignature = (in_oAnnouncement, in_sLocale) => {
  const sMaintenanceStart = normalizeDateString(in_oAnnouncement?.maintenanceStartDate || in_oAnnouncement?.startDate)
  const sTitle = in_oAnnouncement?.title?.[in_sLocale] || in_oAnnouncement?.title?.en || ''
  const sContent = in_oAnnouncement?.content?.[in_sLocale] || in_oAnnouncement?.content?.en || ''

  return `${sMaintenanceStart || ''}|${sTitle.substring(0, 100)}|${sContent.substring(0, 100)}`
}
const shouldSkipAnnouncementFromLocalPreference = (in_sStorageKey, in_sExpectedExpiresAt, in_sExpectedMaintenanceSignature) => {
  const sNormalizedExpectedExpiresAt = normalizeDateString(in_sExpectedExpiresAt)

  if (!sNormalizedExpectedExpiresAt) {
    try {
      localStorage.removeItem(in_sStorageKey)
    } catch (oError) {
      console.warn('[Announcements] Impossible de nettoyer la préférence locale:', oError)
    }
    return false
  }

  const nExpectedTimestamp = new Date(sNormalizedExpectedExpiresAt).getTime()

  let sRawPreference
  try {
    sRawPreference = localStorage.getItem(in_sStorageKey)
  } catch (oError) {
    console.warn('[Announcements] Impossible de lire la préférence locale:', oError)
    return false
  }

  if (!sRawPreference) return false

  let oParsedPreference
  try {
    oParsedPreference = JSON.parse(sRawPreference)
  } catch (oError) {
    try {
      localStorage.removeItem(in_sStorageKey)
    } catch (oRemoveError) {
      console.warn('[Announcements] Impossible de supprimer la préférence locale corrompue:', oRemoveError)
    }
    return false
  }

  const sStoredExpiresAt = normalizeDateString(oParsedPreference?.expiresAt)
  const nStoredTimestamp = sStoredExpiresAt ? new Date(sStoredExpiresAt).getTime() : Number.NaN
  const sStoredMaintenanceSignature = oParsedPreference?.maintenanceSignature

  if (!oParsedPreference?.dismissed || Number.isNaN(nStoredTimestamp)) {
    try {
      localStorage.removeItem(in_sStorageKey)
    } catch (oError) {
      console.warn('[Announcements] Impossible de supprimer la préférence locale invalide:', oError)
    }
    return false
  }

  if (sStoredMaintenanceSignature !== in_sExpectedMaintenanceSignature) {
    try {
      localStorage.removeItem(in_sStorageKey)
    } catch (oError) {
      console.warn('[Announcements] Impossible de supprimer la préférence locale obsolète:', oError)
    }
    return false
  }

  if (Date.now() > nStoredTimestamp) {
    try {
      localStorage.removeItem(in_sStorageKey)
    } catch (oError) {
      console.warn('[Announcements] Impossible de supprimer la préférence locale expirée:', oError)
    }
    return false
  }

  if (nStoredTimestamp !== nExpectedTimestamp || sStoredExpiresAt !== sNormalizedExpectedExpiresAt) {
    try {
      localStorage.setItem(in_sStorageKey, JSON.stringify({
        dismissed: true,
        expiresAt: sNormalizedExpectedExpiresAt,
        maintenanceSignature: in_sExpectedMaintenanceSignature
      }))
    } catch (oError) {
      console.warn('[Announcements] Impossible de synchroniser la date d’expiration locale:', oError)
    }
  }

  return true
}


const getLocalizedValue = (in_oObj, in_sLocale) => {
  if (!in_oObj) return ''
  return in_oObj[in_sLocale] || in_oObj['en'] || Object.values(in_oObj)[0] || ''
}

export const displayNextAnnouncement = (in_dispatch, in_aAnnouncements, in_nIndex) => {
  if (!in_aAnnouncements || in_nIndex >= in_aAnnouncements.length) {
    in_dispatch({ type: 'CLEAR_POPUP' })
    return
  }

  const oAnnouncement = in_aAnnouncements[in_nIndex]
  const sLocale = locale()
  const sDismissStorageKey = getAnnouncementDismissStorageKey(oAnnouncement)
  const sDismissExpiresAt = getAnnouncementDismissExpiresAt(oAnnouncement)
  const sDismissMaintenanceSignature = getAnnouncementMaintenanceSignature(oAnnouncement, sLocale)

  const bShouldSkip = shouldSkipAnnouncementFromLocalPreference(
    sDismissStorageKey,
    sDismissExpiresAt,
    sDismissMaintenanceSignature
  )

  if (bShouldSkip) {
    displayNextAnnouncement(in_dispatch, in_aAnnouncements, in_nIndex + 1)
    return
  }


  const sLocalizedTitle = getLocalizedValue(oAnnouncement.title, sLocale)
  const sLocalizedContent = getLocalizedValue(oAnnouncement.content, sLocale)

  const fnNext = () => {
    displayNextAnnouncement(in_dispatch, in_aAnnouncements, in_nIndex + 1)
  }

  in_dispatch({
    type: 'SET_POPUP',
    payload: {
      type: 'announcement',
      message: sLocalizedContent,
      title: sLocalizedTitle || ihm_viewer__dictionary.MAINTENANCE.TITLE[sLocale] || ihm_viewer__dictionary.MAINTENANCE.TITLE['en'],
      confirmText: ihm_viewer__dictionary.MAINTENANCE.CONTINUE[sLocale] || ihm_viewer__dictionary.MAINTENANCE.CONTINUE['en'],
      dismissStorageKey: sDismissStorageKey,
      dismissExpiresAt: sDismissExpiresAt,
      dismissMaintenanceSignature: sDismissMaintenanceSignature,
      onConfirm: fnNext,
      onClose: fnNext
    }
  })
}

export const loadAndDisplayAnnouncements = async (in_dispatch) => {
  try {
    const oResponse = await fetch(`${sGatewayUrl}/announcements/active`)
    if (!oResponse.ok) {
      const sText = await oResponse.text()
      throw new Error(`HTTP ${oResponse.status} - ${sText}`)
    }

    const oData = await oResponse.json()
    
    if (oData.success && oData.data && oData.data.length > 0) {
      displayNextAnnouncement(in_dispatch, oData.data, 0)
    }
  } catch (oError) {
    console.error('[Announcements] Erreur chargement:', oError)
  }
}

export const useViewerAnnouncements = () => {
  const oDispatch = useDispatch()

  useEffect(() => {
    loadAndDisplayAnnouncements(oDispatch)
  }, [oDispatch])
}
